import { useState, useRef, useEffect, CSSProperties, FC, ReactNode } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────
export type ErrorSeverity = "error" | "warning" | "info";
export interface ValidationMessage { type: ErrorSeverity; msg: string; }
export interface Shortcut { label: string; get: () => [Date, Date]; }
export interface Constraints {
  noFuture: boolean; noPast: boolean; noWeekends: boolean;
  maxDays: number | null; minDays: number | null;
}
export interface Theme {
  accent: string; accentLight: string; dark: boolean;
  bg: string; bgAlt: string; border: string; borderLight: string; sidebar: string;
  text: string; textSub: string; textMuted: string; textLight: string; textLighter: string;
  pageBg: string;
}
export interface DateRangeValue {
  start: Date;
  end: Date | null;
  startTime: string;
  endTime: string;
}
export type DropdownPosition = "left" | "right" | "center";
export interface DateRangePickerProps {
  defaultValue?: Partial<DateRangeValue>;
  onChange?: (value: DateRangeValue) => void;
  defaultOpen?: boolean;
  defaultHideTime?: boolean;
  position?: DropdownPosition;
  alwaysOpen?: boolean;
}
type DisabledReason = "future" | "past" | "weekend" | "maxrange" | false;
type ActivePreset = number | "custom" | null;

// ─── Constants ───────────────────────────────────────────────────────────────
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const ACCENTS = [
  { label: "Sky", value: "#32A7E8" },
  { label: "Violet", value: "#9C6FE4" },
  { label: "Emerald", value: "#10B981" },
  { label: "Rose", value: "#E84A6F" },
  { label: "Amber", value: "#F59E0B" },
  { label: "Teal", value: "#14B8A6" },
];

const SHORTCUTS: Shortcut[] = [
  { label: "Yesterday", get: () => { const d = off(-1); return [d, d]; } },
  { label: "Today", get: () => { const d = td(); return [d, d]; } },
  { label: "Last 7 days", get: () => [off(-6), td()] },
  { label: "Last week", get: () => { const d = td(), s = off(-d.getDay() - 7), e = new Date(s); e.setDate(s.getDate() + 6); return [s, e]; } },
  { label: "This week", get: () => { const d = td(), s = off(-d.getDay()), e = new Date(s); e.setDate(s.getDate() + 6); return [s, e]; } },
  { label: "Last month", get: () => { const d = td(); return [new Date(d.getFullYear(), d.getMonth() - 1, 1), new Date(d.getFullYear(), d.getMonth(), 0)]; } },
  { label: "This month", get: () => { const d = td(); return [new Date(d.getFullYear(), d.getMonth(), 1), new Date(d.getFullYear(), d.getMonth() + 1, 0)]; } },
  { label: "Last 3 months", get: () => [off(-90), td()] },
  { label: "Last 6 months", get: () => [off(-182), td()] },
  { label: "Last year till Date", get: () => { const y = td().getFullYear() - 1; return [new Date(y, 0, 1), td()]; } },
  { label: "This year till Date", get: () => { const y = td().getFullYear(); return [new Date(y, 0, 1), td()]; } },
];

// ─── Theme factory ────────────────────────────────────────────────────────────
function makeTheme(accent: string, dark: boolean): Theme {
  if (dark) return {
    accent, accentLight: accent + "26", dark: true,
    bg: "#1e1e2e", bgAlt: "#181825", border: "#313244", borderLight: "#2e3047",
    sidebar: "#181828", text: "#f0f3fa", textSub: "#cdd6f4",
    textMuted: "#a6adc8", textLight: "#9399b2", textLighter: "#9399b2", pageBg: "#11111b",
  };
  return {
    accent, accentLight: accent + "1f", dark: false,
    bg: "#ffffff", bgAlt: "#fafafa", border: "#e4e4e4", borderLight: "#f0f0f0",
    sidebar: "#ffffff", text: "#111111", textSub: "#444444",
    textMuted: "#777777", textLight: "#999999", textLighter: "#cccccc", pageBg: "#efefef",
  };
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────
function td(): Date { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function off(n: number): Date { const d = td(); d.setDate(d.getDate() + n); return d; }
function pad(n: number): string { return String(n).padStart(2, "0"); }
function sameDay(a: Date | null, b: Date | null): boolean { return !!(a && b && a.toDateString() === b.toDateString()); }
function between(d: Date, s: Date | null, e: Date | null): boolean {
  if (!s || !e) return false;
  const t = d.getTime(), lo = Math.min(s.getTime(), e.getTime()), hi = Math.max(s.getTime(), e.getTime());
  return t > lo && t < hi;
}
function dayDiff(a: Date, b: Date): number { return Math.round(Math.abs(b.getTime() - a.getTime()) / 86_400_000); }
function toMins(t: string): number { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function fmt(d: Date | null, t: string): string {
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}  ·  ${fmtTime12(t)}`;
}
function fmtDate(d: Date | null): string {
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}
function isWeekend(d: Date): boolean { const day = d.getDay(); return day === 0 || day === 6; }
function getWeekNumber(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}
function fmtTime12(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${pad(m)} ${ampm}`;
}

// Count only non-disabled days in [start, end] inclusive
function countActiveDays(start: Date, end: Date, c: Constraints): number {
  const today = td();
  let n = 0;
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const fin = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= fin) {
    const off =
      (c.noWeekends && isWeekend(cur)) ||
      (c.noFuture && cur > today) ||
      (c.noPast && cur < today && !sameDay(cur, today));
    if (!off) n++;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
}

// Trim a date range's start/end past disabled days at the boundaries
function trimToActive(a: Date, b: Date, c: Constraints): [Date, Date] | null {
  const today = td();
  const disabled = (d: Date): boolean =>
    (c.noWeekends && isWeekend(d)) ||
    (c.noFuture && d > today) ||
    (c.noPast && d < today && !sameDay(d, today));
  const s = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const e = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  while (s <= e && disabled(s)) s.setDate(s.getDate() + 1);
  if (s > e) return null;
  while (e >= s && disabled(e)) e.setDate(e.getDate() - 1);
  if (e < s) return null;
  return [s, e];
}

// Return the shortcut index whose trimmed range matches [s,e], or "custom"
function detectShortcut(s: Date | null, e: Date | null, c: Constraints, singleMode: boolean): ActivePreset {
  if (!s) return null;
  for (let i = 0; i < SHORTCUTS.length; i++) {
    const [ra, rb] = SHORTCUTS[i].get();
    const trimmed = trimToActive(ra, rb, c);
    if (!trimmed) continue;
    const [ta, tb] = trimmed;
    if (singleMode) {
      // single-mode: match only single-day shortcuts (Today / Yesterday)
      if (sameDay(ta, tb) && sameDay(s, ta)) return i;
    } else {
      if (e && sameDay(s, ta) && sameDay(e, tb)) return i;
    }
  }
  return "custom";
}

// ─── Validation engine ────────────────────────────────────────────────────────
function getErrors(
  start: Date | null, end: Date | null, startTime: string, endTime: string,
  constraints: Constraints, singleMode: boolean, picking: boolean,
): ValidationMessage[] {
  const errors: ValidationMessage[] = [];
  const { noFuture, noPast, noWeekends, maxDays, minDays } = constraints;
  if (!singleMode && start && !end && !picking) return errors;
  const effectiveEnd: Date | null = singleMode ? start : end;
  if (!effectiveEnd) return errors;
  if (noFuture && start! > td()) errors.push({ type: "error", msg: "Start date cannot be in the future." });
  if (noFuture && effectiveEnd > td()) errors.push({ type: "error", msg: "End date cannot be in the future." });
  if (noPast && start! < td() && !sameDay(start, td())) errors.push({ type: "error", msg: "Start date cannot be in the past." });
  if (noPast && effectiveEnd < td() && !sameDay(effectiveEnd, td())) errors.push({ type: "error", msg: "End date cannot be in the past." });
  if (noWeekends && isWeekend(start!)) errors.push({ type: "error", msg: "Start date falls on a weekend." });
  if (noWeekends && isWeekend(effectiveEnd)) errors.push({ type: "error", msg: "End date falls on a weekend." });
  if (!singleMode && start && effectiveEnd) {
    const diff = dayDiff(start, effectiveEnd);
    if (maxDays && diff > maxDays) errors.push({ type: "error", msg: `Range exceeds maximum of ${maxDays} days (${diff} selected).` });
    if (minDays && diff < minDays && !sameDay(start, effectiveEnd)) errors.push({ type: "error", msg: `Range is below minimum of ${minDays} days (${diff} selected).` });
    if (sameDay(start, effectiveEnd) && minDays && minDays > 0) errors.push({ type: "warning", msg: "Start and end are the same day — zero-duration range." });
  }
  if (!singleMode && start && effectiveEnd && sameDay(start, effectiveEnd)) {
    if (toMins(endTime) <= toMins(startTime)) errors.push({ type: "error", msg: "End time must be after start time on the same day." });
  }
  return errors;
}

// ─── Popup ────────────────────────────────────────────────────────────────────
interface PopupProps { children: ReactNode; onClose: () => void; T: Theme; }

const Popup: FC<PopupProps> = ({ children, onClose, T }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return (
    <div ref={ref} style={{
      position: "absolute", top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)",
      zIndex: 200, background: T.bg, border: `1px solid ${T.border}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 14,
    }}>
      {children}
    </div>
  );
};

// ─── Month Picker ─────────────────────────────────────────────────────────────
interface MonthPickerProps { month: number; onChange: (m: number) => void; onClose: () => void; T: Theme; }

const MonthPicker: FC<MonthPickerProps> = ({ month, onChange, onClose, T }) => (
  <Popup onClose={onClose} T={T}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2, width: 180 }}>
      {MONTHS_SHORT.map((m, i) => (
        <button key={m} onClick={() => { onChange(i); onClose(); }} style={{
          background: month === i ? T.accent : "none", border: "none", padding: "8px 6px",
          fontSize: 14, color: month === i ? "#fff" : T.textMuted, cursor: "pointer",
          fontFamily: "inherit", fontWeight: month === i ? 500 : 400, transition: "all 0.1s",
        }}>{m}</button>
      ))}
    </div>
  </Popup>
);

// ─── Year Picker ──────────────────────────────────────────────────────────────
interface YearPickerProps { year: number; onChange: (y: number) => void; onClose: () => void; T: Theme; }

const YearPicker: FC<YearPickerProps> = ({ year, onChange, onClose, T }) => {
  const [page, setPage] = useState<number>(Math.floor(year / 12) * 12);
  const navBtn: CSSProperties = { background: "none", border: "none", cursor: "pointer", fontSize: 24, color: T.textLighter, padding: "0 4px", fontFamily: "inherit", lineHeight: 1 };
  return (
    <Popup onClose={onClose} T={T}>
      <div style={{ width: 180 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <button onClick={() => setPage(p => p - 12)} style={navBtn}>‹</button>
          <span style={{ fontSize: 12, color: T.textLight }}>{page}–{page + 11}</span>
          <button onClick={() => setPage(p => p + 12)} style={navBtn}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2 }}>
          {Array.from({ length: 12 }, (_, i) => page + i).map(y => (
            <button key={y} onClick={() => { onChange(y); onClose(); }} style={{
              background: year === y ? T.accent : "none", border: "none", padding: "8px 6px",
              fontSize: 14, color: year === y ? "#fff" : T.textMuted, cursor: "pointer",
              fontFamily: "inherit", fontWeight: year === y ? 500 : 400, transition: "all 0.1s",
            }}>{y}</button>
          ))}
        </div>
      </div>
    </Popup>
  );
};

// ─── Calendar ─────────────────────────────────────────────────────────────────
interface CalendarProps {
  year: number; month: number;
  onNav: (dir: 1 | -1) => void;
  onMonthChange: (m: number) => void;
  onYearChange: (y: number) => void;
  start: Date | null; end: Date | null; hover: Date | null;
  onDay: (date: Date) => void; onHover: (date: Date | null) => void;
  singleMode: boolean; constraints: Constraints; showWeekNumbers: boolean; T: Theme;
}

const Calendar: FC<CalendarProps> = ({
  year, month, onNav, onMonthChange, onYearChange,
  start, end, hover, onDay, onHover, singleMode, constraints, showWeekNumbers, T,
}) => {
  const [showMonth, setShowMonth] = useState(false);
  const [showYear, setShowYear] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const { noFuture, noPast, noWeekends, maxDays } = constraints;

  const first = new Date(year, month, 1).getDay();
  const total = new Date(year, month + 1, 0).getDate();
  const eff = hover && start && !end ? hover : end;
  const rawCells: (Date | null)[] = [...Array(first).fill(null), ...Array.from({ length: total }, (_, i) => new Date(year, month, i + 1))];
  while (rawCells.length % 7 !== 0) rawCells.push(null);
  const rows: (Date | null)[][] = [];
  for (let i = 0; i < rawCells.length; i += 7) rows.push(rawCells.slice(i, i + 7));
  const cols = showWeekNumbers ? "28px repeat(7,1fr)" : "repeat(7,1fr)";

  const getDisabledReason = (date: Date): DisabledReason => {
    if (noFuture && date > td()) return "future";
    if (noPast && date < td() && !sameDay(date, td())) return "past";
    if (noWeekends && isWeekend(date)) return "weekend";
    if (maxDays && start && !end && dayDiff(start, date) > maxDays) return "maxrange";
    return false;
  };

  const navBtn: CSSProperties = { background: "none", border: "none", cursor: "pointer", fontSize: 20, color: T.textLighter, padding: "4px", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" };
  const hdrBtn: CSSProperties = { background: "none", border: "none", cursor: "pointer", fontSize: 16, fontWeight: 600, color: T.text, padding: "0 2px 2px", fontFamily: "inherit", borderBottom: `1px dashed ${T.dark ? T.accent : "#7ec8f1"}`, lineHeight: 1.3 };

  const renderDay = (date: Date | null, fallbackKey: string) => {
    if (!date) return <div key={fallbackKey} />;
    const dk = date.toISOString();
    const dr = getDisabledReason(date);
    const isStart = sameDay(date, start);
    const isEnd = sameDay(date, eff);
    const inRange = !singleMode && between(date, start, eff);
    const isToday = sameDay(date, td());
    const sel = isStart || isEnd;
    const capL = !singleMode && isStart && !!eff && !sameDay(start, eff);
    const capR = !singleMode && isEnd && !!start && !sameDay(start, eff);
    const hovered = hoveredKey === dk && !dr && !sel;
    return (
      <div key={dk} style={{ position: "relative", height: 38, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {(inRange || capL || capR) && (
          <div style={{
            position: "absolute", top: 0, bottom: 0,
            left: capL ? "50%" : 0, right: capR ? "50%" : 0,
            background: T.accentLight, zIndex: 0
          }} />
        )}
        <button
          className="drp-day-btn"
          onClick={() => !dr && onDay(date)}
          onMouseEnter={() => { setHoveredKey(dk); if (!dr) onHover(date); }}
          onMouseLeave={() => { setHoveredKey(null); onHover(null); }}
          aria-label={date.toDateString()} aria-disabled={!!dr} aria-selected={sel}
          title={dr === "future" ? "Future dates not allowed" : dr === "past" ? "Past dates not allowed" : dr === "weekend" ? "Weekends not available" : dr === "maxrange" ? `Exceeds max range of ${maxDays} days` : undefined}
          style={{
            position: "relative", zIndex: 1,
            width: 36, height: 36, borderRadius: "50%",
            boxShadow: isToday && !sel ? `inset 0 0 0 1.5px ${T.accent}` : "none",
            border: "none",
            background: dr
              ? (dr === "maxrange" ? (T.dark ? "#3a1a1a" : "#fff5f5") : "transparent")
              : sel ? T.accent
                : hovered ? (T.dark ? "#2e3054" : "#f5f5f5")
                  : "transparent",
            color: dr ? (T.dark ? "#4a4a6a" : "#d0d0d0") : sel ? "#fff" : isToday ? T.accent : T.text,
            fontSize: 15, fontWeight: sel ? 600 : 400, cursor: dr ? "not-allowed" : "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.1s",
            textDecoration: dr === "weekend" && !sel ? "line-through" : "none",
            opacity: dr && dr !== "maxrange" ? 0.35 : 1,
          }}
        >{date.getDate()}</button>
      </div>
    );
  };

  return (
    <div style={{ flex: 1, width: showWeekNumbers ? 336 : 302, minWidth: showWeekNumbers ? 336 : 302 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button onClick={() => onNav(-1)} style={navBtn}>‹</button>
        <div style={{ display: "flex", gap: 8, position: "relative", alignItems: "baseline" }}>
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowMonth(v => !v); setShowYear(false); }} style={hdrBtn}>{MONTHS_FULL[month]}</button>
            {showMonth && <MonthPicker month={month} onChange={m => { onMonthChange(m); setShowMonth(false); }} onClose={() => setShowMonth(false)} T={T} />}
          </div>
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowYear(v => !v); setShowMonth(false); }} style={hdrBtn}>{year}</button>
            {showYear && <YearPicker year={year} onChange={y => { onYearChange(y); setShowYear(false); }} onClose={() => setShowYear(false)} T={T} />}
          </div>
        </div>
        <button onClick={() => onNav(1)} style={navBtn}>›</button>
      </div>

      {/* Day-of-week labels (+ optional week-number header) */}
      <div style={{ display: "grid", gridTemplateColumns: cols, marginBottom: 4 }}>
        {showWeekNumbers && (
          <div style={{ textAlign: "center", fontSize: 11, color: T.textLighter, paddingBottom: 8, fontWeight: 600, opacity: 0.7 }}>#</div>
        )}
        {DAYS.map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 13, color: T.textLighter, paddingBottom: 8 }}>{d}</div>
        ))}
      </div>

      {/* Day grid — row by row so we can prepend the week number */}
      {rows.map((row, ri) => (
        <div key={ri} style={{ display: "grid", gridTemplateColumns: cols, marginBottom: 2 }}>
          {showWeekNumbers && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 34, fontSize: 11, color: T.textLighter, fontWeight: 500, opacity: 0.7 }}>
              {(() => { const d = row.find(x => x !== null); return d ? getWeekNumber(d) : ""; })()}
            </div>
          )}
          {row.map((date, ci) => renderDay(date, `_${ri * 7 + ci}`))}
        </div>
      ))}
    </div>
  );
};

// ─── Time Input ───────────────────────────────────────────────────────────────
interface TimeInputProps { label: string; value: string; onChange: (val: string) => void; hasError: boolean; disabled?: boolean; T: Theme; }

const TimeInput: FC<TimeInputProps> = ({ label, value, onChange, hasError, disabled = false, T }) => {
  const [h24, m] = value.split(":").map(Number);
  const isPM = h24 >= 12;
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;

  const toH24 = (h: number, pm: boolean): number => pm ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
  const setH12 = (v: number) => {
    let c = v; if (c > 12) c = 1; if (c < 1) c = 12;
    onChange(`${pad(toH24(c, isPM))}:${pad(m)}`);
  };
  const setM = (v: number) => {
    let c = v; if (c > 59) c = 0; if (c < 0) c = 59;
    onChange(`${pad(h24)}:${pad(c)}`);
  };
  const toggleAMPM = () => onChange(`${pad(isPM ? h24 - 12 : h24 + 12)}:${pad(m)}`);

  const boxStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: T.bg,
    border: `1px solid ${hasError ? "#e05252" : T.border}`,
    borderRadius: 6,
    padding: "4px 2px",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const numStyle: CSSProperties = {
    width: "100%",
    minWidth: 0,
    border: "none",
    background: "transparent",
    fontSize: 14,
    fontWeight: 500,
    color: hasError ? "#e05252" : T.text,
    outline: "none",
    fontFamily: "inherit",
    cursor: disabled ? "not-allowed" : "text",
    textAlign: "center",
    boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, opacity: disabled ? 0.4 : 1, transition: "opacity 0.15s", width: "100%" }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: hasError ? "#e05252" : (T.dark ? "#a6adc8" : T.textLighter), letterSpacing: "0.12em", textTransform: "uppercase" }}>
        {label}
      </div>

      {/* HH & MM side-by-side row */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
        {/* HH Box */}
        <div style={{ flex: "1 1 0px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 500, color: T.dark ? "#7f849c" : T.textLighter, letterSpacing: "0.08em", textAlign: "center" }}>HH</span>
          <div style={boxStyle}>
            <input
              type="number"
              value={pad(h12)}
              onChange={e => !disabled && setH12(Number(e.target.value))}
              readOnly={disabled}
              style={numStyle}
            />
          </div>
        </div>

        <span style={{ fontSize: 13, fontWeight: 600, color: T.textLighter, marginTop: 12, flexShrink: 0 }}>:</span>

        {/* MM Box */}
        <div style={{ flex: "1 1 0px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 500, color: T.dark ? "#7f849c" : T.textLighter, letterSpacing: "0.08em", textAlign: "center" }}>MM</span>
          <div style={boxStyle}>
            <input
              type="number"
              value={pad(m)}
              onChange={e => !disabled && setM(Number(e.target.value))}
              readOnly={disabled}
              style={numStyle}
            />
          </div>
        </div>
      </div>

      {/* AM / PM Segmented Control */}
      <div style={{ display: "flex", borderRadius: 6, border: `1px solid ${T.border}`, overflow: "hidden", marginTop: 2 }}>
        {(["AM", "PM"] as const).map(period => {
          const active = period === "AM" ? !isPM : isPM;
          return (
            <button
              key={period}
              onClick={() => { if (!disabled && (period === "PM") !== isPM) toggleAMPM(); }}
              style={{
                flex: 1,
                padding: "5px 0",
                border: "none",
                background: active ? T.accent : "transparent",
                color: active ? "#ffffff" : T.textMuted,
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                cursor: disabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
            >
              {period}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Error Banner ─────────────────────────────────────────────────────────────
interface ErrorBannerProps { errors: ValidationMessage[]; T: Theme; }

const ErrorBanner: FC<ErrorBannerProps> = ({ errors, T }) => {
  if (!errors.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "12px 26px", background: T.bgAlt, borderTop: `1px solid ${T.borderLight}` }}>
      {errors.map((e, i) => {
        const isErr = e.type === "error";
        const isWarn = e.type === "warning";
        const color = isErr ? "#c0392b" : isWarn ? "#b7811a" : "#6b7280";
        const bg = isErr ? (T.dark ? "#3a1515" : "#fff5f5") : isWarn ? (T.dark ? "#3a2e0a" : "#fffbea") : (T.dark ? "#1e2230" : "#f8f9fb");
        const bdr = isErr ? "#fad2d2" : isWarn ? "#f5dea3" : "#e8eaed";
        const icon = isErr ? "✕" : isWarn ? "⚠" : "ℹ";
        return (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, background: bg, border: `1px solid ${bdr}`, padding: "8px 12px" }}>
            <span style={{ fontSize: 14, color, marginTop: 1, flexShrink: 0, fontWeight: 600 }}>{icon}</span>
            <span style={{ fontSize: 14, color, lineHeight: 1.5 }}>{e.msg}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Toggle ───────────────────────────────────────────────────────────────────
interface ToggleProps { label: string; description?: string; checked: boolean; onChange: () => void; T: Theme; }

const Toggle: FC<ToggleProps> = ({ label, description, checked, onChange, T }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 14, cursor: "pointer", padding: "12px 0", borderBottom: `1px solid ${T.borderLight}` }}>
    <div onClick={onChange} style={{
      marginTop: 2, width: 30, height: 17, borderRadius: 9,
      background: checked ? T.accent : (T.dark ? "#3a3a5c" : "#e0e0e0"),
      position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s",
    }}>
      <div style={{ position: "absolute", top: 2, left: checked ? 13 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left 0.18s", boxShadow: "0 1px 3px rgba(0,0,0,0.18)" }} />
    </div>
    <div>
      <div style={{ fontSize: 14, color: T.text }}>{label}</div>
      {description && <div style={{ fontSize: 13, color: T.textLight, marginTop: 3 }}>{description}</div>}
    </div>
  </label>
);

// ─── Stepper ──────────────────────────────────────────────────────────────────
interface StepperProps { value: number; onChange: (v: number) => void; min?: number; max?: number; T: Theme; }

const Stepper: FC<StepperProps> = ({ value, onChange, min = 1, max = 999, T }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
    <button onClick={() => onChange(Math.max(min, value - 1))} style={{ background: "none", border: `1px solid ${T.border}`, width: 26, height: 26, cursor: "pointer", fontSize: 14, color: T.textLight, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>−</button>
    <input
      type="number" value={value} min={min} max={max}
      onChange={e => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
      style={{ width: 50, textAlign: "center", border: `1px solid ${T.border}`, padding: "3px 4px", fontSize: 14, color: T.text, fontFamily: "inherit", outline: "none", background: T.bg }}
    />
    <button onClick={() => onChange(Math.min(max, value + 1))} style={{ background: "none", border: `1px solid ${T.border}`, width: 26, height: 26, cursor: "pointer", fontSize: 14, color: T.textLight, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>+</button>
    <span style={{ fontSize: 13, color: T.textLighter, letterSpacing: "0.06em" }}>DAYS</span>
  </div>
);

// ─── DateRangePicker ──────────────────────────────────────────────────────────
const DateRangePicker: FC<DateRangePickerProps> = ({ defaultValue, onChange, defaultOpen = false, defaultHideTime = false, position: defaultPosition, alwaysOpen: defaultAlwaysOpen = true }) => {
  const now = td();

  // ── Picker state ─────────────────────────────────────────────────────────────
  const [start, setStart] = useState<Date | null>(() => defaultValue?.start ?? SHORTCUTS[4].get()[0]);
  const [end, setEnd] = useState<Date | null>(() => defaultValue?.end ?? SHORTCUTS[4].get()[1]);
  const [hover, setHover] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>(defaultValue?.startTime ?? "09:00");
  const [endTime, setEndTime] = useState<string>(defaultValue?.endTime ?? "17:00");
  const [picking, setPicking] = useState<boolean>(false);
  const [lm, setLm] = useState<number>(now.getMonth());
  const [ly, setLy] = useState<number>(now.getFullYear());
  const [active, setActive] = useState<ActivePreset>(defaultValue ? null : 4);

  // ── Config state ──────────────────────────────────────────────────────────────
  const [alwaysOpen, setAlwaysOpen] = useState<boolean>(defaultAlwaysOpen);
  const [hidePresets, setHidePresets] = useState(false);
  const [hideTime, setHideTime] = useState(defaultHideTime);
  const [hideTwoMonths, setHideTwoMonths] = useState(false);
  const [singleMode, setSingleMode] = useState(false);
  const [noFuture, setNoFuture] = useState(false);
  const [noPast, setNoPast] = useState(false);
  const [noWeekends, setNoWeekends] = useState(false);
  const [showWeekNumbers, setShowWeekNumbers] = useState(false);
  const [useMaxDays, setUseMaxDays] = useState(false);
  const [maxDays, setMaxDays] = useState(30);
  const [useMinDays, setUseMinDays] = useState(false);
  const [minDays, setMinDays] = useState(2);
  const [position, setPosition] = useState<DropdownPosition>(defaultPosition ?? "center");

  // ── Theme state ───────────────────────────────────────────────────────────────
  const [darkMode, setDarkMode] = useState(false);
  const [accent, setAccent] = useState(ACCENTS[0].value);
  const T = makeTheme(accent, darkMode);

  // ── Mobile detection ─────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Dropdown open state ───────────────────────────────────────────────────────
  const [isOpenState, setIsOpenState] = useState(defaultOpen);
  const isOpen = alwaysOpen || isOpenState;
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (alwaysOpen || !isOpenState || isMobile) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpenState(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [alwaysOpen, isOpenState, isMobile]);

  // ── Smart Dropdown Clamping ───────────────────────────────────────────────────
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({ left: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (!isOpen || isMobile || !containerRef.current) return;
      const triggerRect = containerRef.current.getBoundingClientRect();
      const presetsW = (!hidePresets && !isMobile) ? 184 : 0;
      const timeW = (!hideTime && !isMobile) ? 140 : 0;
      const calW = hideTwoMonths ? 410 : 680;
      const cardWidth = calW + presetsW + timeW;
      const sidebarWidth = 440;
      const maxAllowedRight = window.innerWidth - sidebarWidth - 16;
      const minAllowedLeft = 16;

      let targetLeft: number;
      if (position === "right") {
        targetLeft = triggerRect.right - cardWidth;
      } else if (position === "center") {
        targetLeft = triggerRect.left + (triggerRect.width / 2) - (cardWidth / 2);
      } else {
        targetLeft = triggerRect.left;
      }

      // 1. Cap right boundary so card right edge never overlaps configuration sidebar
      let clampedLeft = Math.min(targetLeft, maxAllowedRight - cardWidth);
      // 2. Cap left boundary so card left edge never overflows screen left
      clampedLeft = Math.max(minAllowedLeft, clampedLeft);

      const relativeLeft = clampedLeft - triggerRect.left;

      setDropdownStyle({
        left: relativeLeft,
        maxWidth: Math.max(320, maxAllowedRight - clampedLeft),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [isOpen, isMobile, position, hidePresets, hideTime, hideTwoMonths, showWeekNumbers, alwaysOpen]);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const rm: number = lm === 11 ? 0 : lm + 1;
  const ry: number = lm === 11 ? ly + 1 : ly;

  const constraints: Constraints = {
    noFuture, noPast, noWeekends,
    maxDays: useMaxDays ? maxDays : null,
    minDays: useMinDays ? minDays : null,
  };

  const errors = getErrors(start, end, startTime, endTime, constraints, singleMode, picking);
  const hasErrors = errors.some(e => e.type === "error");
  const timeConflict = !singleMode && !!start && !!end && sameDay(start, end) && toMins(endTime) <= toMins(startTime);
  const canApply = (singleMode ? !!start : (!!start && !!end)) && !hasErrors;

  // Day counts for display — active = non-disabled days only
  const calDayCount = (start && end) ? dayDiff(start, end) + 1 : 0;
  const activeDayCount = (start && end) ? countActiveDays(start, end, constraints) : 0;
  const hasSkipped = activeDayCount < calDayCount;

  // ── Navigation ────────────────────────────────────────────────────────────────
  const nav = (dir: 1 | -1): void => {
    let m = lm + dir, y = ly;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setLm(m); setLy(y);
  };
  const setLeftMonth = (m: number): void => setLm(m);
  const setLeftYear = (y: number): void => setLy(y);
  const setRightMonth = (m: number): void => { let nl = m - 1, ny = ry; if (nl < 0) { nl = 11; ny--; } setLm(nl); setLy(ny); };
  const setRightYear = (y: number): void => setLy(rm === 0 ? y - 1 : y);

  // ── Selection ─────────────────────────────────────────────────────────────────
  const pickDay = (date: Date): void => {
    if (singleMode) {
      setStart(date); setEnd(null); setPicking(false);
      setActive(detectShortcut(date, null, constraints, true));
      return;
    }
    if (!picking || !start) {
      setStart(date); setEnd(null); setPicking(true); setActive("custom");
    } else {
      // Resolve final [newStart, newEnd] before committing state, so detectShortcut sees them
      const ns = date < start ? date : start;
      const ne = date < start ? start : date;
      if (date < start) { setEnd(start); setStart(date); } else setEnd(date);
      setPicking(false);
      setActive(detectShortcut(ns, ne, constraints, false));
    }
  };

  const pickShortcut = (s: Shortcut, i: number): void => {
    let [a, b] = s.get();
    const trimmed = trimToActive(a, b, constraints);
    if (!trimmed) return;           // every day in range is disabled — skip
    [a, b] = trimmed;
    setStart(a); setEnd(b); setPicking(false); setActive(i);
    setLm(a.getMonth()); setLy(a.getFullYear());
  };

  const clear = (): void => { setStart(null); setEnd(null); setPicking(false); setActive(null); };

  // ── Inline toggle knob (used for Max/Min range) ───────────────────────────────
  const knob = (on: boolean, onClick: () => void) => (
    <div onClick={onClick} style={{ marginTop: 2, width: 30, height: 17, borderRadius: 9, background: on ? T.accent : (T.dark ? "#3a3a5c" : "#e0e0e0"), position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s" }}>
      <div style={{ position: "absolute", top: 2, left: on ? 13 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left 0.18s", boxShadow: "0 1px 3px rgba(0,0,0,0.18)" }} />
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  const startErr = start && errors.some(e => e.type === "error" && e.msg.toLowerCase().includes("start"));
  const endErr = end && errors.some(e => e.type === "error" && e.msg.toLowerCase().includes("end"));

  const triggerLabel = (() => {
    if (!start) return null;
    const s = hideTime ? fmtDate(start) : fmt(start, startTime);
    if (singleMode || !end) return s;
    return s + "  →  " + (hideTime ? fmtDate(end) : fmt(end, endTime));
  })();

  const handleApply = () => {
    if (!canApply || !start) return;
    onChange?.({ start, end: singleMode ? null : end, startTime, endTime });
    if (!alwaysOpen) setIsOpenState(false);
  };

  const presetsW = (!hidePresets && !isMobile) ? 184 : 0;
  const timeW = (!hideTime && !isMobile) ? 140 : 0;
  const calW = hideTwoMonths ? 410 : 680;
  const cardWidth = calW + presetsW + timeW;

  const pickerCard = (
    <div style={{
      background: T.bg,
      borderRadius: isMobile ? 0 : 12,
      border: `1px solid ${T.borderLight}`,
      display: "flex", flexDirection: "column",
      width: isMobile ? "100%" : cardWidth,
      maxWidth: isMobile ? "100%" : (dropdownStyle.maxWidth ?? "100%"),
      overflow: "hidden",
      boxShadow: isMobile ? "none" : "0 10px 10px rgba(0,0,0,.04), 0 20px 25px rgba(0,0,0,.10)",
      transition: "background 0.2s, border-color 0.2s",
    }}>

      {/* ── Sidebar + Calendars ── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", position: "relative" }}>

        {/* Desktop: absolute sidebar */}
        {!hidePresets && !isMobile && (
          <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 184, borderRight: `1px solid ${T.borderLight}`, display: "flex", flexDirection: "column", background: T.sidebar, zIndex: 1, transition: "background 0.2s" }}>
            <div style={{ padding: "20px 20px 8px", fontSize: 11, fontWeight: 500, color: T.dark ? "#a6adc8" : T.textLighter, letterSpacing: "0.16em", textTransform: "uppercase", flexShrink: 0 }}>Presets</div>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", paddingBottom: 10 }}>
              {SHORTCUTS.map((s, i) => (
                <button key={s.label} className="drp-preset-btn" onClick={() => pickShortcut(s, i)} style={{
                  background: active === i ? T.accentLight : "none", border: "none", padding: "7px 20px", textAlign: "left", fontSize: 14,
                  color: active === i ? T.accent : (T.dark ? "#a6adc8" : "#454545"), fontWeight: active === i ? 600 : 400,
                  cursor: "pointer", fontFamily: "inherit",
                  borderLeft: active === i ? `3px solid ${T.accent}` : "3px solid transparent",
                  transition: "all 0.1s", flexShrink: 0,
                }}>{s.label}</button>
              ))}
              <div style={{ height: 1, background: T.borderLight, margin: "7px 0", flexShrink: 0 }} />
              <button className="drp-preset-btn" onClick={() => { clear(); setActive("custom"); }} style={{
                background: active === "custom" ? T.accentLight : "none", border: "none", padding: "7px 20px", textAlign: "left", fontSize: 14,
                color: active === "custom" ? T.accent : (T.dark ? "#a6adc8" : "#454545"), fontWeight: active === "custom" ? 600 : 400,
                cursor: "pointer", fontFamily: "inherit",
                borderLeft: active === "custom" ? `3px solid ${T.accent}` : "3px solid transparent",
                transition: "all 0.1s", flexShrink: 0,
              }}>Custom Date</button>
            </div>
          </div>
        )}

        {/* Mobile: horizontal chip strip */}
        {!hidePresets && isMobile && (
          <div className="drp-shortcuts-strip" style={{
            overflowX: "auto", display: "flex", alignItems: "center",
            padding: "10px 14px", gap: 6,
            borderBottom: `1px solid ${T.borderLight}`,
            background: T.sidebar,
          }}>
            {SHORTCUTS.map((s, i) => (
              <button key={s.label} className="drp-preset-btn" onClick={() => pickShortcut(s, i)} style={{
                background: active === i ? T.accentLight : T.bgAlt,
                border: `1px solid ${active === i ? T.accent : T.border}`,
                borderRadius: 20, padding: "5px 12px", fontSize: 14,
                color: active === i ? T.accent : (T.dark ? "#a6adc8" : "#454545"), fontWeight: active === i ? 600 : 400,
                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0,
              }}>{s.label}</button>
            ))}
            <div style={{ width: 1, height: 20, background: T.border, flexShrink: 0 }} />
            <button className="drp-preset-btn" onClick={() => { clear(); setActive("custom"); }} style={{
              background: active === "custom" ? T.accentLight : T.bgAlt,
              border: `1px solid ${active === "custom" ? T.accent : T.border}`,
              borderRadius: 20, padding: "5px 12px", fontSize: 14,
              color: active === "custom" ? T.accent : (T.dark ? "#a6adc8" : "#454545"), fontWeight: active === "custom" ? 600 : 400,
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0,
            }}>Custom</button>
          </div>
        )}

        {/* Calendars */}
        <div style={{ display: "flex", flexDirection: "column", flex: 1, marginLeft: (!hidePresets && !isMobile) ? 184 : 0, marginRight: (!hideTime && !isMobile) ? 140 : 0 }}>
          <div style={{ display: "flex", justifyContent: hideTwoMonths ? "center" : undefined, padding: isMobile ? "20px 16px 16px" : "24px", gap: isMobile ? 0 : 28 }}>
            <Calendar year={ly} month={lm} onNav={nav} onMonthChange={setLeftMonth} onYearChange={setLeftYear} start={start} end={end} hover={hover} onDay={pickDay} onHover={setHover} singleMode={singleMode} constraints={constraints} showWeekNumbers={showWeekNumbers} T={T} />
            {!hideTwoMonths && !isMobile && (
              <Calendar year={ry} month={rm} onNav={nav} onMonthChange={setRightMonth} onYearChange={setRightYear} start={start} end={end} hover={hover} onDay={pickDay} onHover={setHover} singleMode={singleMode} constraints={constraints} showWeekNumbers={showWeekNumbers} T={T} />
            )}
          </div>
        </div>

        {/* Desktop: right time panel (Panel 3) */}
        {!hideTime && !isMobile && (
          <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 140, borderLeft: `1px solid ${T.borderLight}`, display: "flex", flexDirection: "column", background: T.sidebar, zIndex: 1, transition: "background 0.2s" }}>
            <div style={{ padding: "20px 14px 8px", fontSize: 11, fontWeight: 500, color: T.dark ? "#a6adc8" : T.textLighter, letterSpacing: "0.16em", textTransform: "uppercase", flexShrink: 0 }}>Time</div>
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", padding: "10px 14px", gap: 10 }}>
              <TimeInput label="START TIME" value={startTime} onChange={setStartTime} hasError={timeConflict} T={T} />
              {!singleMode && (
                <>
                  <div style={{ height: 1, background: T.borderLight, margin: "2px 0" }} />
                  <TimeInput label="END TIME" value={endTime} onChange={setEndTime} hasError={timeConflict} disabled={!end} T={T} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Mobile: horizontal time strip below calendars */}
        {!hideTime && isMobile && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", padding: "12px 16px", borderTop: `1px solid ${T.borderLight}`, background: T.sidebar }}>
            <TimeInput label="START TIME" value={startTime} onChange={setStartTime} hasError={timeConflict} T={T} />
            {!singleMode && <TimeInput label="END TIME" value={endTime} onChange={setEndTime} hasError={timeConflict} disabled={!end} T={T} />}
          </div>
        )}
      </div>

      {/* Validation banners */}
      <ErrorBanner errors={errors} T={T} />

      {/* Bottom bar */}
      <div style={{
        borderTop: `1px solid ${T.borderLight}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "space-between",
        padding: isMobile ? "14px 16px" : "16px 24px",
        gap: isMobile ? 12 : 24,
      }}>

        {!isMobile && (
          <div style={{ flex: 1, padding: hideTime ? "0" : "0 12px", minWidth: 0, overflow: "hidden" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: T.dark ? "#a6adc8" : T.textLighter, letterSpacing: "0.16em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              <span>{singleMode ? "SELECTED" : "SELECTION"}</span>
              {!singleMode && start && end && !hasErrors && (
                <span>
                  · {activeDayCount} day{activeDayCount !== 1 ? "s" : ""}
                  {hasSkipped && (
                    <span style={{ fontSize: 11, color: T.dark ? "#7f849c" : T.textLighter, marginLeft: 4 }}>({calDayCount} total)</span>
                  )}
                </span>
              )}
            </div>
            <div style={{
              fontSize: hideTwoMonths ? 14 : 16,
              fontWeight: 600,
              color: T.text,
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
              whiteSpace: "nowrap"
            }}>
              <span style={{ color: startErr ? "#c0392b" : T.text, whiteSpace: "nowrap" }}>
                {hideTime ? fmtDate(start) : fmt(start, startTime)}
              </span>
              {!singleMode && (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.dark ? "#9399b2" : T.textLighter} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, margin: "0 2px" }}>
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                  <span style={{ color: endErr ? "#c0392b" : T.text, whiteSpace: "nowrap" }}>
                    {hideTime ? fmtDate(end) : fmt(end, endTime)}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {isMobile && !singleMode && start && end && !hasErrors && (
          <div style={{ fontSize: 11, fontWeight: 500, color: T.dark ? "#a6adc8" : T.textLighter, letterSpacing: "0.16em", textTransform: "uppercase" }}>
            {activeDayCount} day{activeDayCount !== 1 ? "s" : ""} selected
            {hasSkipped && ` (${calDayCount} total)`}
          </div>
        )}

        <div style={{ display: "flex", gap: hideTwoMonths ? 8 : 12, flexShrink: 0 }}>
          <button onClick={clear} style={{
            background: T.dark ? "#26283b" : T.bg, border: `1px solid ${T.dark ? "#3d405b" : T.border}`,
            padding: hideTwoMonths ? "9px 18px" : "11px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, color: T.dark ? "#cdd6f4" : "#595959",
            cursor: "pointer", fontFamily: "inherit",
            flex: isMobile ? 1 : undefined,
          }}>Clear</button>
          <button
            disabled={!canApply}
            onClick={handleApply}
            title={hasErrors ? "Fix errors before applying" : !start ? "Select a date first" : !end && !singleMode ? "Select an end date" : ""}
            style={{
              background: canApply ? T.accent : (T.dark ? "#2a2a40" : "#f0f0f0"),
              border: "1px solid transparent", padding: hideTwoMonths ? "9px 22px" : "11px 30px", borderRadius: 8, fontSize: 14,
              color: canApply ? "#fff" : T.textLighter,
              cursor: canApply ? "pointer" : "not-allowed",
              fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s",
              flex: isMobile ? 2 : undefined,
            }}
          >Apply</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh", background: T.pageBg,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: "'Segoe UI Variable Static Display',system-ui,sans-serif",
      padding: isMobile ? 0 : "48px 440px 48px 0",
      flexDirection: isMobile ? "column" : "row",
      position: "relative",
      transition: "background 0.2s",
    }}>
      <style>{`
        @import url('https://fonts.cdnfonts.com/css/segoe-ui-variable-static-display');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; margin: 0; }
        button:focus { outline: none; }
        button:not(.drp-day-btn):not(.drp-preset-btn):hover { opacity: 0.7; }
        .drp-preset-btn:hover { background-color: ${T.dark ? "rgba(255,255,255,0.06)" : "#f5f5f5"} !important; color: ${T.dark ? "#ffffff" : "#262626"} !important; opacity: 1 !important; }
        .drp-day-btn:hover { opacity: 1; }
        .drp-shortcuts-strip { scrollbar-width: none; }
        .drp-shortcuts-strip::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Picker column ── */}
      <div ref={containerRef} style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "stretch" : "center", width: isMobile ? "100%" : undefined, position: "relative" }}>
        {!isMobile && <div style={{ fontSize: 12, color: T.textLight, letterSpacing: "0.12em", marginBottom: 12, textTransform: "uppercase", width: "100%" }}>Date Range Picker</div>}

        {/* Trigger input */}
        <div
          onClick={() => {
            if (!alwaysOpen) setIsOpenState(v => !v);
          }}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "0 12px", height: 42,
            border: `1.5px solid ${isOpen ? T.accent : T.border}`,
            background: T.bg,
            cursor: "pointer",
            transition: "border-color 0.15s, background 0.2s",
            minWidth: isMobile ? undefined : 300,
            width: isMobile ? "100%" : undefined,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: isOpen ? T.accent : T.textMuted }}>
            <rect x="1" y="2" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M1 6h14" stroke="currentColor" strokeWidth="1.4" />
            <path d="M5 1v2M11 1v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <span style={{
            flex: 1, fontSize: 14,
            color: triggerLabel ? T.text : T.textLighter,
            userSelect: "none", whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {triggerLabel ?? "Select date range..."}
          </span>
          {(start || end) && (
            <button
              onClick={(e) => { e.stopPropagation(); clear(); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: T.textLighter, fontSize: 18, padding: "0 2px",
                display: "flex", alignItems: "center", lineHeight: 1,
                fontFamily: "inherit", flexShrink: 0,
              }}
            >×</button>
          )}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, color: T.textMuted, transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Desktop dropdown */}
        {isOpen && !isMobile && (
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: dropdownStyle.left,
            zIndex: 1000,
          }}>
            {pickerCard}
          </div>
        )}
      </div>

      {/* Mobile full-screen overlay */}
      {isOpen && isMobile && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: T.pageBg, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 16px", borderBottom: `1px solid ${T.borderLight}`,
            background: T.bg, flexShrink: 0,
          }}>
            <button
              onClick={() => setIsOpenState(false)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: T.text, fontFamily: "inherit", fontSize: 14,
                display: "flex", alignItems: "center", gap: 6, padding: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>
            <span style={{ flex: 1, textAlign: "center", fontSize: 14, fontWeight: 500, color: T.text }}>Select Date Range</span>
          </div>
          {pickerCard}
        </div>
      )}

      {/* ── Config panel ── */}
      <div style={{
        position: isMobile ? "static" : "fixed",
        top: 0, right: 0, bottom: 0,
        width: isMobile ? "100%" : 440,
        height: isMobile ? undefined : "100vh",
        background: T.bg,
        borderLeft: isMobile ? "none" : `1px solid ${T.borderLight}`,
        boxShadow: isMobile ? "none" : "-4px 0 24px rgba(0,0,0,0.06)",
        display: "flex", flexDirection: "column",
        zIndex: 100,
        overflowY: "auto",
        transition: "background 0.2s, border-color 0.2s",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${T.borderLight}`,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.textLight, letterSpacing: "0.12em", textTransform: "uppercase" }}>Configuration</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>Always open</span>
            {knob(alwaysOpen, () => setAlwaysOpen(v => { const next = !v; if (!next) setIsOpenState(false); return next; }))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>

          {/* Display + Constraints */}
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>
            <div style={{
              flex: 1, padding: "0 20px",
              borderRight: isMobile ? "none" : `1px solid ${T.borderLight}`,
              borderBottom: isMobile ? `1px solid ${T.borderLight}` : "none",
            }}>
              <div style={{ padding: "14px 0 6px", fontSize: 12, color: T.textLighter, letterSpacing: "0.14em", textTransform: "uppercase" }}>Display</div>
              <Toggle label="Hide presets" description="Remove the shortcut sidebar" checked={hidePresets} onChange={() => setHidePresets(v => !v)} T={T} />
              <Toggle label="Hide time inputs" description="Date-only, no hour/minute" checked={hideTime} onChange={() => setHideTime(v => !v)} T={T} />
              <Toggle label="Single month view" description="One calendar instead of two" checked={hideTwoMonths} onChange={() => setHideTwoMonths(v => !v)} T={T} />
              <Toggle label="Single date" description="Pick one date, not a range" checked={singleMode} onChange={() => { setSingleMode(v => !v); clear(); }} T={T} />
            </div>
            <div style={{ flex: 1, padding: "0 20px" }}>
              <div style={{ padding: "14px 0 6px", fontSize: 12, color: T.textLighter, letterSpacing: "0.14em", textTransform: "uppercase" }}>Constraints</div>
              <Toggle label="Disable future dates" description="Block dates after today" checked={noFuture} onChange={() => { setNoFuture(v => !v); clear(); }} T={T} />
              <Toggle label="Disable past dates" description="Block dates before today" checked={noPast} onChange={() => { setNoPast(v => !v); clear(); }} T={T} />
              <Toggle label="Disable weekends" description="Sat and Sun unavailable" checked={noWeekends} onChange={() => { setNoWeekends(v => !v); clear(); }} T={T} />
              <Toggle label="Show week numbers" description="ISO week number column in calendar" checked={showWeekNumbers} onChange={() => setShowWeekNumbers(v => !v)} T={T} />
            </div>
          </div>

          {/* Max + Min range */}
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", borderTop: `1px solid ${T.borderLight}` }}>
            <div style={{
              flex: 1, padding: "14px 20px",
              borderRight: isMobile ? "none" : `1px solid ${T.borderLight}`,
              borderBottom: isMobile ? `1px solid ${T.borderLight}` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                {knob(useMaxDays, () => { setUseMaxDays(v => !v); clear(); })}
                <div>
                  <div style={{ fontSize: 14, color: T.text }}>Max range</div>
                  <div style={{ fontSize: 13, color: T.textLight, marginTop: 3 }}>Limit selectable days</div>
                </div>
              </div>
              {useMaxDays && <Stepper value={maxDays} onChange={setMaxDays} min={1} T={T} />}
            </div>
            <div style={{ flex: 1, padding: "14px 20px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                {knob(useMinDays, () => { setUseMinDays(v => !v); clear(); })}
                <div>
                  <div style={{ fontSize: 14, color: T.text }}>Min range</div>
                  <div style={{ fontSize: 13, color: T.textLight, marginTop: 3 }}>Require minimum days</div>
                </div>
              </div>
              {useMinDays && <Stepper value={minDays} onChange={setMinDays} min={1} T={T} />}
            </div>
          </div>

          {/* Position */}
          <div style={{ borderTop: `1px solid ${T.borderLight}`, padding: "0 20px 14px" }}>
            <div style={{ padding: "14px 0 6px", fontSize: 12, color: T.textLighter, letterSpacing: "0.14em", textTransform: "uppercase" }}>Position</div>
            <div style={{ fontSize: 13, color: T.textLight, marginBottom: 10 }}>Dropdown alignment relative to trigger</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(["left", "center", "right"] as const).map(pos => (
                <button
                  key={pos}
                  onClick={() => setPosition(pos)}
                  style={{
                    background: position === pos ? T.accent : T.bgAlt,
                    border: `1px solid ${position === pos ? T.accent : T.border}`,
                    borderRadius: 6,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: position === pos ? 600 : 400,
                    color: position === pos ? "#fff" : T.text,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.15s",
                  }}
                >
                  {pos === "center" ? "Center (default)" : pos === "left" ? "Left" : "Right"}
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div style={{ borderTop: `1px solid ${T.borderLight}`, padding: "0 20px" }}>
            <div style={{ padding: "14px 0 6px", fontSize: 12, color: T.textLighter, letterSpacing: "0.14em", textTransform: "uppercase" }}>Theme</div>
            <Toggle label="Dark mode" description="Switch to dark colour scheme" checked={darkMode} onChange={() => setDarkMode(v => !v)} T={T} />
            <div style={{ padding: "14px 0" }}>
              <div style={{ fontSize: 13, color: T.textLight, marginBottom: 10 }}>Accent colour</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {ACCENTS.map(a => (
                  <button
                    key={a.value}
                    title={a.label}
                    onClick={() => setAccent(a.value)}
                    style={{
                      width: 26, height: 26, borderRadius: "50%", background: a.value,
                      border: "none", cursor: "pointer", padding: 0, flexShrink: 0,
                      boxShadow: accent === a.value ? `0 0 0 2px ${T.bg}, 0 0 0 4px ${a.value}` : "none",
                      transition: "box-shadow 0.15s",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer: Supported Frameworks */}
          <div style={{
            borderTop: `1px solid ${T.borderLight}`,
            padding: "14px 20px",
            background: T.sidebar,
            flexShrink: 0,
            marginTop: "auto",
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.dark ? "#7f849c" : T.textLighter, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
              Supported Stack
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {[
                {
                  name: "React 18+",
                  svg: (
                    <svg width="13" height="13" viewBox="-11.5 -10.23174 23 20.46348" fill="none" style={{ flexShrink: 0 }}>
                      <circle cx="0" cy="0" r="2" fill="#61DAFB"/>
                      <g stroke="#61DAFB" strokeWidth="1">
                        <ellipse rx="11" ry="4.2"/>
                        <ellipse rx="11" ry="4.2" transform="rotate(60)"/>
                        <ellipse rx="11" ry="4.2" transform="rotate(120)"/>
                      </g>
                    </svg>
                  )
                },
                {
                  name: "Next.js",
                  svg: (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0, color: T.text }}>
                      <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.02 18.27L9.83 8.78v8.66H8.25V6.85h1.58l7.19 9.49v-9.49h1.58v11.42h-1.58z"/>
                    </svg>
                  )
                },
                {
                  name: "Vue 3",
                  svg: (
                    <svg width="13" height="12" viewBox="0 0 261.76 226.69" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M161.096 0L130.88 52.349L100.664 0H0l130.88 226.69L261.76 0h-100.664z" fill="#41B883"/>
                      <path d="M161.096 0L130.88 52.349L100.664 0H52.352l78.528 136.01L209.408 0h-48.312z" fill="#34495E"/>
                    </svg>
                  )
                },
                {
                  name: "Angular 14+",
                  svg: (
                    <svg width="12" height="12" viewBox="0 0 250 250" fill="none" style={{ flexShrink: 0 }}>
                      <polygon points="125,30 125,30 125,30 31.9,63.2 46.1,186.3 125,230 125,230 125,230 203.9,186.3 218.1,63.2" fill="#DD0031"/>
                      <polygon points="125,30 125,52.2 125,52.1 125,153.4 125,153.4 170.8,153.4 182.6,181.7 203.9,186.3 218.1,63.2" fill="#C3002F"/>
                      <path d="M125,52.1L66.8,181.7h21.4l11.7-29.2h50.3l11.7,29.2h21.4L125,52.1z M125,83.9l17.8,44.4h-35.6L125,83.9z" fill="#FFFFFF"/>
                    </svg>
                  )
                },
                {
                  name: "Svelte",
                  svg: (
                    <svg width="12" height="13" viewBox="0 0 512 512" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M433.8 111.4c-28.7-41.2-80.1-59.5-131-46.7L128.9 108c-37.4 9.4-66.2 38.6-75.1 76.2-9.6 40.8.6 83 27.2 113.8L35.2 360.7c-7.9 12-11.4 26.5-9.8 40.9 2.8 25.1 21.6 45.4 46.5 50.1l173.9 33.3c48.8 9.3 98.7-8.9 129.5-47.3l45.4-56.7c37.5-46.9 39.5-112.5 4.8-161.7l3.3-3.9z" fill="#FF3E00"/>
                    </svg>
                  )
                },
              ].map(stack => (
                <span key={stack.name} style={{
                  fontSize: 11, fontWeight: 500, padding: "4px 9px", borderRadius: 12,
                  background: T.bgAlt, border: `1px solid ${T.borderLight}`,
                  color: T.dark ? "#cdd6f4" : T.textSub, display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                  {stack.svg}
                  {stack.name}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: T.dark ? "#7f849c" : T.textLighter }}>
              Zero external dependencies · 100% TypeScript
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
